"use client";

import React, { useEffect } from "react";
import { Formik, Form } from "formik";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  insertTestimonial,
  updateTestimonial,
  findAllTestimonial,
  findByIDTestimonial,
} from "./testimonial.service";
import { testimonialValidationSchema } from "./testimonial.validation";
import { clearTestimonialDetails } from "./testimonial.slice";
import { findAllActiveCustomer } from "@/modules/customer/customer.service";
import { findAllActiveProduct } from "@/modules/product/product.service";
import { closed } from "@/redux/slices/drawerSlice";
import { STATUS_OPTIONS } from "@/lib/constants";
import { InputField } from "@/components/common/FormFields/InputField";
import { SelectField } from "@/components/common/FormFields/SelectField";
import { TextAreaField } from "@/components/common/FormFields/TextAreaField";
import { CustomButton } from "@/components/common/CustomButton";
import { Save, X, Star } from "lucide-react";
import { FormLoader } from "@/components/common/FormLoader";

const RATING_OPTIONS = [
  { label: "5 Stars (⭐⭐⭐⭐⭐ - Excellent)", value: 5 },
  { label: "4 Stars (⭐⭐⭐⭐ - Good)", value: 4 },
  { label: "3 Stars (⭐⭐⭐ - Average)", value: 3 },
  { label: "2 Stars (⭐⭐ - Poor)", value: 2 },
  { label: "1 Star (⭐ - Terrible)", value: 1 },
];

const TestimonialForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const { TestimonialDetail, formLoading, loading } = useAppSelector(
    (state) => state.testimonial,
  );
  const { CustomerList: activeCustomers } = useAppSelector(
    (state) => state.customer,
  );
  const { ProductList: activeProducts } = useAppSelector(
    (state) => state.product,
  );
  const { user, loginData } = useAppSelector((state) => state.auth);
  const { data: TestimonialID } = useAppSelector((state) => state.drawer);

  useEffect(() => {
    dispatch(findAllActiveCustomer());
    dispatch(findAllActiveProduct());
  }, [dispatch]);

  useEffect(() => {
    if (TestimonialID) {
      dispatch(findByIDTestimonial(TestimonialID));
    }
  }, [TestimonialID, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearTestimonialDetails());
    };
  }, [dispatch]);

  const customerOptions = [
    { label: "-- Select Customer (Optional) --", value: "" },
    ...(activeCustomers || []).map((c: any) => ({
      label: `${c.FullName || "Customer"} ${c.MobileNo ? `(${c.MobileNo})` : ""}`,
      value: String(c.ID || c.CustomerID),
    })),
  ];

  const productOptions = [
    { label: "-- Select Associated Product (Optional) --", value: "" },
    ...(activeProducts || []).map((p: any) => ({
      label: p.ProductName || p.Name || `Product #${p.ID}`,
      value: String(p.ID || p.ProductID),
    })),
  ];

  const handleSubmit = async (values: any) => {
    const custID = values?.customerID ? Number(values.customerID) : null;
    const prodID = values?.productID ? Number(values.productID) : null;
    const ratingNum = Number(values?.rating) || 5;
    const userId = Number(user?.id) || Number(loginData?.ID) || 1;

    if (TestimonialID) {
      const payload = {
        id: TestimonialID,
        customerID: custID,
        productID: prodID,
        customerName: values?.customerName?.trim() || "",
        rating: ratingNum,
        description: values?.description?.trim() || "",
        isActive: values?.isActive === "true" || values?.isActive === true,
      };
      await dispatch(updateTestimonial(payload));
    } else {
      const payload = {
        customerID: custID,
        productID: prodID,
        customerName: values?.customerName?.trim() || "",
        rating: ratingNum,
        description: values?.description?.trim() || "",
        isActive: values?.isActive === "true" || values?.isActive === true,
        createdBy: userId,
      };
      await dispatch(insertTestimonial(payload));
    }

    dispatch(findAllTestimonial());
    dispatch(closed());
  };

  return (
    <div className="relative">
      <FormLoader loading={formLoading || loading} />
      <Formik
        initialValues={{
          customerID: TestimonialDetail?.CustomerID
            ? String(TestimonialDetail.CustomerID)
            : "",
          productID: TestimonialDetail?.ProductID
            ? String(TestimonialDetail.ProductID)
            : "",
          customerName: TestimonialDetail?.CustomerName ?? "",
          rating: TestimonialDetail?.Rating ?? 5,
          description: TestimonialDetail?.Description ?? "",
          isActive: TestimonialDetail?.IsActive ?? true,
        }}
        validationSchema={testimonialValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue }) => (
          <Form className="flex flex-col gap-6">
            <div className="space-y-4">
              {/* Select Customer */}
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold block">
                  Select Registered Customer
                </label>
                <select
                  name="customerID"
                  value={values.customerID}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFieldValue("customerID", val);
                    if (val) {
                      const selected = (activeCustomers || [])?.find(
                        (c: any) => String(c?.ID || c?.CustomerID) === val,
                      );
                      setFieldValue("customerName", selected?.FullName || "");
                    } else {
                      setFieldValue("customerName", "");
                    }
                  }}
                  className="w-full px-4 py-2.5 border border-slate-200 bg-slate-50 text-slate-800 text-xs rounded-lg outline-none focus:border-gold-core focus:bg-white transition-all font-medium"
                >
                  {customerOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Customer Name */}
              <InputField
                label="Customer Name"
                name="customerName"
                placeholder="Enter Customer Name"
                disabled={Boolean(values?.customerID)}
                required
              />

              {/* Associated Product */}
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold block">
                  Associated Product (Optional)
                </label>
                <select
                  name="productID"
                  value={values.productID}
                  onChange={(e) => setFieldValue("productID", e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-200 bg-slate-50 text-slate-800 text-xs rounded-lg outline-none focus:border-gold-core focus:bg-white transition-all font-medium"
                >
                  {productOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Rating */}
              <SelectField
                label="Rating"
                name="rating"
                options={RATING_OPTIONS}
                required
              />

              {/* Description */}
              <TextAreaField
                label="Testimonial / Review Description"
                name="description"
                placeholder="Enter client review or feedback details..."
                rows={4}
                required
              />

              {/* Status */}
              <SelectField
                label="Status"
                name="isActive"
                options={STATUS_OPTIONS}
                required
              />
            </div>

            {/* Bottom Actions */}
            <div className="flex items-center gap-3 pt-6 mt-6 border-t border-slate-100">
              <CustomButton
                type="submit"
                variant="primary"
                loading={formLoading}
                icon={<Save size={14} />}
                fullWidth
              >
                {TestimonialID ? "Update" : "Insert"}
              </CustomButton>
              <CustomButton
                type="button"
                variant="outline"
                icon={<X size={14} />}
                onClick={() => dispatch(closed())}
                disabled={formLoading}
                fullWidth
              >
                Cancel
              </CustomButton>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default TestimonialForm;
